import React, { useContext, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { DestinationsPicker, PaxPicker } from "../widgets";
import _ from "lodash";
import { createUseStyles } from "react-jss";
import { StyleTypeContext, TranslationContext } from "../../../umbilical_tools";
import { DateTime } from "luxon";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form } from "formik";
import { DatepickerField } from "../../forms/inputGroup/helpers";
import { useHistory } from "react-router-dom";
import { NoScrollContext } from "../../../context";
import { validateActivitySearch } from "./helpers";
import {
  activitySearchFormReset,
  activitySearchFormUpdate,
  activitySearchRequest,
  filtersReset,
  searchResultsReset,
  selectedActivityReset,
} from "../../../actions/search/activities_search";
import jssVariables from "../../../jss_setup";
import { useApoAuth } from "../../../hooks";
import { SearchButton } from "../../common/Button";

export function useActivitySearch() {
  const dispatch = useDispatch();
  const onFormUpdate = useCallback(
    ({ form }) => dispatch(activitySearchFormUpdate({ form })),
    [dispatch],
  );

  const onFormSearch = useCallback(
    ({ source_entity, market_type, history, setLoadingSearchBtn }) =>
      dispatch(
        activitySearchRequest({
          source_entity,
          market_type,
          history,
          setLoadingSearchBtn,
        }),
      ),
    [dispatch],
  );
  return { onFormUpdate, onFormSearch };
}

const styles = createUseStyles({
  activitySearchForm: (props) => ({
    display: "grid",
    gridTemplateRows: "repeat(3, max-content)",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    padding: jssVariables.normal_gap,
    alignItems: "center",
    ..._.get(props, "customStyles.activitySearchForm", {}),
  }),
  datePickerContainer: (props) => ({
    display: "grid",
    gridTemplateRows: "max-content max-content",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    ..._.get(props, "customStyles.datePickerContainer", {}),
  }),
  datePickerLabel: (props) => ({
    textTransform: "uppercase",
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.datePickerLabel", {}),
  }),
  datePicker: (props) => ({
    width: "100%",
    cursor: "pointer",
    height: "2.9rem",
    borderRadius: "5px",
    border: "none",
    color: jssVariables.colors.text.dark,
    background: "#ffffff",
    textAlign: "left",
    fontSize: "medium",
    paddingLeft: ".5rem",
    "& input": { color: jssVariables.colors.text.dark },
    ..._.get(props, "customStyles.datePicker", {}),
  }),
  searchBtnContr: (props) => ({
    justifySelf: "center",
    paddingTop: jssVariables.normal_gap,
    ..._.get(props, "customStyles.searchBtnContr", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    activitySearchForm: (props) => ({
      gridTemplateRows: "unset",
      gridTemplateColumns: "repeat(3, 1fr) max-content",
      alignItems: "end",
      gridGap: `calc(${jssVariables.normal_gap} / 2)`,
      padding: 0,
      ..._.get(props, "customStyles.activitySearchForm.smallscreen", {}),
    }),
    datePickerContainer: (props) => ({
      ..._.get(props, "customStyles.datePickerContainer.smallscreen", {}),
    }),
    datePickerLabel: (props) => ({
      ..._.get(props, "customStyles.datePickerLabel.smallscreen", {}),
    }),
    datePicker: (props) => ({
      color: jssVariables.colors.darkTextColor,
      ..._.get(props, "customStyles.datePicker.smallscreen", {}),
    }),
  },
});
export const ActivitySearchForm = ({ setShowPaxPickerModal }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const history = useHistory();
  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const { source_entity, market_type } = useApoAuth();

  const customStyles = cStyler({
    type,
    brand,
    objName: "actMainSearchFormStyles",
  });
  const classes = styles({ customStyles });

  const search_form = useSelector((state) => state.ActivitySearchForm);
  const { date_from, date_to } = search_form;

  const { t } = useContext(TranslationContext);
  const { toggleNoScroll } = useContext(NoScrollContext);

  const dispatch = useDispatch();

  const { onFormUpdate, onFormSearch } = useActivitySearch();

  const onClearState = useCallback(() => {
    dispatch(activitySearchFormReset());
    dispatch(filtersReset());
    dispatch(searchResultsReset());
    dispatch(selectedActivityReset());
  }, [dispatch]);

  function onClickPax() {
    setShowPaxPickerModal(true);
  }

  useEffect(() => {
    onClearState();
  }, []);

  return (
    <Formik
      initialValues={{
        destinationObj: {
          destination_code: _.get(search_form, "destination_code", []),
          destination_meta: _.get(search_form, "destination_meta", {}),
        },
        datesArray: [date_from, date_to],
      }}
      onSubmit={async (values) => {
        if (!isSubmitting) {
          setIsSubmitting(true);

          const error_msgs = validateActivitySearch({ values });
          if (error_msgs.length) {
            setIsSubmitting(false);
            return;
          }

          const [date_from, date_to] = values.datesArray;

          await onFormUpdate({
            form: {
              date_from,
              date_to,
              destination_code: [...values.destinationObj.destination_code],
              destination_meta: {
                ...values.destinationObj.destination_meta,
              },
            },
          });
          await onFormSearch({ source_entity, market_type, history });
        }
      }}
    >
      <Form className={classes.activitySearchForm}>
        <DestinationsPicker
          id="destinationsActPicker"
          name="destinationObj"
          types={["DEST"]}
          product_type={"ACT"}
          placeholder={t("destinationPlaceholder")}
          autocomplete={true}
          value={_.get(search_form, "destination_code", [])}
          customStyles={_.get(customStyles, "inputGroupField", {})}
        />
        <DatepickerField
          id="checkInOutActDatepicker"
          label={t("dates")}
          name="datesArray"
          selectsRange={true}
          dateFormat="MMM d, yyyy"
          minDate={DateTime.now().toISODate()}
          maxDate={DateTime.now().plus({ days: 365 }).toISODate()}
          placeholder={"Mon DD, YYYY - Mon DD, YYYY"}
          withPortal={true}
          customStyles={_.get(customStyles, "inputGroupField", {})}
        />
        <PaxPicker
          id="paxPickerActBtn"
          adults={search_form.adults}
          childs={search_form.children}
          children_ages={search_form.children_ages}
          onClick={onClickPax}
          product_type="ACT"
          customStyles={_.get(customStyles, "inputGroupField", {})}
        />
        <div className={classes.searchBtnContr}>
          <SearchButton
            id="actSearchBtn"
            text={t("search")}
            loading={isSubmitting}
            customStyles={_.get(customStyles, "searchBtn", {})}
          />
        </div>
      </Form>
    </Formik>
  );
};
ActivitySearchForm.defaultProps = {
  customStyles: {},
  max_rooms_allowed: 0,
};
ActivitySearchForm.propTypes = {
  customStyles: PropTypes.object.isRequired,
  max_rooms_allowed: PropTypes.number.isRequired,
  setShowPaxPickerModal: PropTypes.func.isRequired,
};
